import React from 'react'
import { Route, useRouteMatch } from 'react-router-dom'
import { useLocation, useHistory } from 'react-router'
import { isEmpty } from 'ramda'
import { csx } from '@vtex/admin-ui'
import { useFeatureFlag } from '@/core/hooks'

import {
  ConfirmPostalCodeStep,
  ProfileButton,
  CheckoutContainer,
  CloseButton,
  SalesAssociateCodeStep,
} from './components'
import {
  HomeDeliveryScreen,
  ClientPickupFromStoreScreen,
  OrderSummaryScreen,
  ShippingMethodSelectionScreen,
  TakeAwayScreen,
  AdvertisementsScreen,
  CartScreen,
  AssemblyItems,
} from './screens'
import {
  TopbarLeftArea,
  TopbarRightArea,
} from '../Instore/components/Header/Topbar'
import { useCheckout } from './state'
import { ExitCartDrawer } from './screens/OrderSummary/components'
import { CheckoutRoute, PostalCodeFlows } from './constants'
import { EditProfileScreen } from './screens/EditProfile'
import { AssemblyItemsProvider } from './screens/Assembly/context/AssemblyItemsContext'
import { useOrderForm } from '../../core/requests/checkout'
import { getSalesAssociateCodeFromOrderForm } from '../../core/utils/orderForm'
import { SearchLocationIcon } from '../Search/components'

export const CheckoutScreen = () => {
  const { path } = useRouteMatch()
  const location = useLocation()
  const history = useHistory()
  const checkout = useCheckout()
  const { orderForm } = useOrderForm()
  const code = getSalesAssociateCodeFromOrderForm(orderForm)
  const { skipLocationSelectionScreen } = useFeatureFlag()

  const clientDataState = checkout.checkout.clientData

  const getPostalCodeNextStep = (
    flow: PostalCodeFlows = PostalCodeFlows.HomeDelivery
  ) => {
    const nextStep = isEmpty(clientDataState.address.neighborhood)
      ? `${path}/${flow}/${CheckoutRoute.AddressNeighborhood}`
      : isEmpty(clientDataState.address.street)
      ? `${path}/${flow}/${CheckoutRoute.AddressStreet}`
      : `${path}/${flow}/${CheckoutRoute.AddressNumber}`

    return nextStep
  }

  const handleCloseSaleDrawerClose = () => {
    history.goBack()
  }

  const handlePostalCodeSubmit = () => {
    const origin = new URLSearchParams(location.search).get('origin')

    let nextStep = `${CheckoutRoute.ShippingMethodSelection}`

    // TODO: change after refactoring of client profile
    if (origin) {
      const flow: PostalCodeFlows = origin.includes(PostalCodeFlows.EditProfile)
        ? PostalCodeFlows.EditProfile
        : PostalCodeFlows.HomeDelivery

      nextStep = `${getPostalCodeNextStep(flow)}`
    }

    history.push(nextStep)
  }

  const handleCloseButtonClick = () => {
    history.push(`${location.pathname}/close-sale`)
  }

  const shouldRenderTopbarLeftArea =
    location.pathname.endsWith('/checkout/shipping-method-selection') &&
    skipLocationSelectionScreen()

  return (
    <CheckoutContainer>
      {shouldRenderTopbarLeftArea && (
        <TopbarLeftArea>
          <SearchLocationIcon />
        </TopbarLeftArea>
      )}
      <TopbarRightArea>
        <div
          className={csx({
            display: 'flex',
            alignItems: 'center',
            float: 'right',
          })}
        >
          <ProfileButton />
          {!location.pathname.includes('services') && (
            <CloseButton onClick={handleCloseButtonClick} />
          )}
        </div>
      </TopbarRightArea>
      <Route path={CheckoutRoute.HomeDelivery}>
        <HomeDeliveryScreen />
      </Route>
      <Route path={CheckoutRoute.EditProfile}>
        {/* TODO: remove when develop new client profile screen */}
        <EditProfileScreen />
      </Route>
      <Route path={CheckoutRoute.Pickup}>
        <ClientPickupFromStoreScreen />
      </Route>
      <Route path={CheckoutRoute.TakeAway}>
        <TakeAwayScreen />
      </Route>
      <Route path={`${path}/sales-associate-step`}>
        <SalesAssociateCodeStep initialSalesAssociateCode={code} />
      </Route>
      <Route path={`${path}/confirm-postal-code`}>
        <ConfirmPostalCodeStep onStepSubmit={handlePostalCodeSubmit} />
      </Route>
      <Route path={`${path}/cart`}>
        <CartScreen />
      </Route>
      <Route path={`${path}/order-summary`}>
        <OrderSummaryScreen />
      </Route>
      <Route path={`${path}/shipping-method-selection/`}>
        <ShippingMethodSelectionScreen />
      </Route>
      <Route path={CheckoutRoute.Advertisements}>
        <AdvertisementsScreen />
      </Route>
      <ExitCartDrawer
        isOpen={location.pathname.includes('close-sale')}
        onClose={handleCloseSaleDrawerClose}
      />
      <Route path={`${path}/services`}>
        <AssemblyItemsProvider>
          <AssemblyItems />
        </AssemblyItemsProvider>
      </Route>
    </CheckoutContainer>
  )
}
